import { useEffect, useMemo, useRef, useState } from "react";
import { askPmsChatbot } from "app/services/chatbotService";
import "./chatbot.css";

const CHATBOT_HISTORY_KEY = "pms-chatbot-history";
const CHATBOT_ICON_SRC = `${process.env.PUBLIC_URL || ""}/chatbot/chatbot-bot-icon.png`;

const INITIAL_MESSAGE = {
  id: "welcome",
  role: "assistant",
  text: "Ask a policy or Aptance question. If you include a policy number, AptaBot will try the policy flow first.",
  sources: [],
  suggestions: [],
  policies: [],
  needsClarification: false,
  originalQuestion: "",
};

function createUserMessage(text) {
  return {
    id: `${Date.now()}-user`,
    role: "user",
    text,
  };
}

function createAssistantMessage({
  text,
  sources = [],
  suggestions = [],
  policies = [],
  needsClarification = false,
  originalQuestion = "",
}) {
  return {
    id: `${Date.now()}-assistant-${Math.random().toString(36).slice(2, 8)}`,
    role: "assistant",
    text,
    sources,
    suggestions,
    policies,
    needsClarification,
    originalQuestion,
  };
}

function getSuggestionText(suggestion) {
  if (typeof suggestion === "string") return suggestion;
  return suggestion?.label || suggestion?.text || suggestion?.title || "";
}

function getInitialMessages() {
  try {
    const stored = sessionStorage.getItem(CHATBOT_HISTORY_KEY);
    if (!stored) return [INITIAL_MESSAGE];

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) && parsed.length ? parsed : [INITIAL_MESSAGE];
  } catch (error) {
    return [INITIAL_MESSAGE];
  }
}

function UserMessageIcon() {
  return (
    <svg className="chatbot-avatar__icon chatbot-avatar__icon--user" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 12.2C14.43 12.2 16.4 10.23 16.4 7.8C16.4 5.37 14.43 3.4 12 3.4C9.57 3.4 7.6 5.37 7.6 7.8C7.6 10.23 9.57 12.2 12 12.2Z"
        fill="currentColor"
      />
      <path
        d="M12 14.2C7.96 14.2 4.68 16.83 4.68 20.08C4.68 20.7 5.18 21.2 5.8 21.2H18.2C18.82 21.2 19.32 20.7 19.32 20.08C19.32 16.83 16.04 14.2 12 14.2Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MessageAvatar({ role }) {
  return (
    <span className={`chatbot-avatar chatbot-avatar--${role}`}>
      {role === "assistant" ? (
        <img className="chatbot-avatar__icon chatbot-avatar__icon--bot" src={CHATBOT_ICON_SRC} alt="" aria-hidden="true" />
      ) : (
        <UserMessageIcon />
      )}
    </span>
  );
}

export default function ChatbotPanel({ isOpen, onClose }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(getInitialMessages);
  const [isSending, setIsSending] = useState(false);
  const panelRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem(CHATBOT_HISTORY_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, isOpen, isSending]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (event.target?.closest(".chatbot-launcher")) {
        return;
      }

      if (!panelRef.current?.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isOpen, onClose]);

  const canSend = useMemo(() => {
    return Boolean(input.trim()) && !isSending;
  }, [input, isSending]);

  const appendAssistantError = (message) => {
    setMessages((prev) =>
      prev.concat(
        createAssistantMessage({
          text: message || "Unable to get chatbot response.",
        })
      )
    );
  };

  const submitQuestion = async (question, selectedPolicyId = null) => {
    const normalizedQuestion = question.trim();

    if (!normalizedQuestion) {
      appendAssistantError("Enter a question before sending.");
      return;
    }

    if (!selectedPolicyId) {
      setMessages((prev) => prev.concat(createUserMessage(normalizedQuestion)));
      setInput("");
    }

    setIsSending(true);

    try {
      const response = await askPmsChatbot(normalizedQuestion, selectedPolicyId, 5);

      setMessages((prev) =>
        prev.concat(
          createAssistantMessage({
            text: response.answer || response.message || "No answer returned.",
            sources: Array.isArray(response.sources) ? response.sources : [],
            suggestions: Array.isArray(response.suggestions) ? response.suggestions : [],
            policies: Array.isArray(response.policies) ? response.policies : [],
            needsClarification: Boolean(response.needsClarification),
            originalQuestion: normalizedQuestion,
          })
        )
      );
    } catch (error) {
      appendAssistantError(error.message || "Unable to get chatbot response.");
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = async () => {
    await submitQuestion(input);
  };

  const handleSuggestionClick = async (suggestion) => {
    const suggestionText = getSuggestionText(suggestion);
    if (!suggestionText) return;
    await submitQuestion(suggestionText);
  };

  const handlePolicySelect = async (question, policyId) => {
    await submitQuestion(question, policyId);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div ref={panelRef} className={`chatbot-panel ${isOpen ? "open" : ""}`}>
      <div className="chatbot-panel__header">
        <div className="chatbot-panel__title">AptaBot</div>
        <button type="button" className="chatbot-panel__close" onClick={onClose} aria-label="Close chatbot">
          ×
        </button>
      </div>

      <div className="chatbot-panel__body" ref={bodyRef}>
        {messages.map((message) => (
          <div key={message.id} className={`chatbot-message chatbot-message--${message.role}`}>
            <div className="chatbot-message__row">
              <MessageAvatar role={message.role} />

              <div className="chatbot-message__content">
                <div className="chatbot-message__bubble">{message.text}</div>

                {message.sources?.length ? (
                  <div className="chatbot-message__sources">
                    {message.sources.map((source, index) => (
                      <span key={`${message.id}-source-${index}`} className="chatbot-chip">
                        {source?.type ? `${source.type}: ` : ""}
                        {source?.label || source?.id || "Source"}
                      </span>
                    ))}
                  </div>
                ) : null}

                {message.needsClarification && message.policies?.length ? (
                  <div className="chatbot-message__policies">
                    {message.policies.map((policy, index) => (
                      <button
                        key={policy.id || `${message.id}-policy-${index}`}
                        type="button"
                        className="chatbot-policy-card"
                        onClick={() => handlePolicySelect(message.originalQuestion || message.text, policy.id)}
                        disabled={isSending}
                      >
                        <span className="chatbot-policy-card__number">{policy.number || "Policy"}</span>
                        <span className="chatbot-policy-card__meta">
                          {policy.customerName || policy.business || policy.insurer || "Select this policy"}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}

                {message.suggestions?.length ? (
                  <div className="chatbot-message__suggestions">
                    {message.suggestions.map((suggestion, index) => (
                      <button
                        key={`${message.id}-suggestion-${index}`}
                        type="button"
                        className="chatbot-suggestion"
                        onClick={() => handleSuggestionClick(suggestion)}
                        disabled={isSending}
                      >
                        {getSuggestionText(suggestion)}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ))}

        {isSending ? <div className="chatbot-state">Thinking...</div> : null}
      </div>

      <div className="chatbot-panel__footer">
        <textarea
          rows={3}
          className="chatbot-panel__input"
          placeholder="Ask about a policy, renewal, coverage, or Aptance workflow..."
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button type="button" className="chatbot-panel__send" disabled={!canSend} onClick={handleSend}>
          {isSending ? "Sending..." : "Send"}
        </button>
      </div>
    </div>
  );
}
